/**
 * Training Data Generator for Phishing Detection
 * Generates synthetic training data based on known phishing patterns
 */

import { featureExtractor } from './featureExtractor.js';

// Sample legitimate URLs
const legitimateUrls = [
  'https://www.google.com/search?q=weather',
  'https://www.amazon.com/dp/B08N5WRWNW',
  'https://github.com/microsoft/vscode',
  'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
  'https://www.facebook.com/profile.php?id=123',
  'https://www.linkedin.com/in/johndoe',
  'https://twitter.com/elonmusk',
  'https://www.netflix.com/browse',
  'https://www.apple.com/iphone',
  'https://www.microsoft.com/en-us/windows',
  'https://www.paypal.com/myaccount/home',
  'https://www.instagram.com/explore',
  'https://www.reddit.com/r/programming',
  'https://stackoverflow.com/questions/tagged/javascript',
  'https://www.dropbox.com/home',
  'https://mail.google.com/mail/u/0',
  'https://drive.google.com/drive/my-drive',
  'https://www.spotify.com/us/premium',
  'https://www.airbnb.com/rooms/12345',
  'https://www.booking.com/hotel/us/example',
  'https://www.ebay.com/itm/123456789',
  'https://www.etsy.com/listing/123456789',
  'https://www.walmart.com/ip/123456789',
  'https://www.target.com/p/example-product',
  'https://www.bestbuy.com/site/example/123456',
  'https://www.homedepot.com/p/Example/123456',
  'https://www.lowes.com/pd/Example/123456',
  'https://www.costco.com/example-product.html',
  'https://www.ikea.com/us/en/p/example-12345',
  'https://www.wayfair.com/furniture/pdp/example',
  'https://www.zappos.com/p/example-shoe/product/123456',
  'https://www.nordstrom.com/s/example/123456',
  'https://www.macys.com/shop/product/example',
  'https://www.sephora.com/product/example-P123456',
  'https://www.ulta.com/p/example-product-pimprod123456',
  'https://www.cvs.com/shop/example-product-prodid-123456',
  'https://www.walgreens.com/store/c/example/ID=prod123456',
  'https://www.rite-aid.com/shop/example-product-123456',
  'https://www.kroger.com/p/example-product/123456789',
  'https://www.wholefoodsmarket.com/products/example'
];

// Sample phishing URLs
const phishingUrls = [
  'http://secure-paypal-login.tk/verify-account',
  'http://192.168.1.1/banking/login.php',
  'http://amaz0n-secure.xyz/signin?ref=urgent',
  'http://google-account-verify.ml/confirm-identity',
  'http://facebook-security-alert.ga/login-now',
  'http://netflix-billing-update.cf/payment-required',
  'http://apple-id-suspended.gq/restore-account',
  'http://microsoft-password-reset.top/verify',
  'http://bank0famerica-secure.work/online-banking',
  'http://wells-fargo-alert.click/account-locked',
  'http://chase-security-notice.link/verify-identity',
  'http://citibank-urgent-action.info/confirm',
  'http://usbank-account-suspended.online/restore',
  'http://capitalone-verify-now.site/login',
  'http://discover-card-alert.website/update-info',
  'http://americanexpress-security.space/verify-card',
  'http://login-paypal-secure.pw/confirm-account',
  'http://signin-amazon-verify.tk/update-payment',
  'http://account-google-security.ml/verify-now',
  'http://facebook-login-verify.ga/confirm-identity',
  'http://instagram-account-alert.cf/restore-access',
  'http://twitter-security-notice.gq/verify-account',
  'http://linkedin-password-reset.xyz/confirm',
  'http://dropbox-storage-full.top/upgrade-now',
  'http://icloud-account-locked.work/verify-identity',
  'http://outlook-mailbox-full.click/clear-storage',
  'http://yahoo-account-suspended.link/restore',
  'http://aol-security-alert.info/verify-now',
  'http://ebay-seller-verification.online/confirm',
  'http://etsy-shop-suspended.site/restore-access',
  'http://192.168.0.1:8080/admin/login.asp',
  'http://10.0.0.1/secure/banking/verify.php',
  'http://xn--pypal-4ve.com/login',
  'http://bit.ly/3xYz123',
  'http://tinyurl.com/suspicious-link',
  'http://secure-login@malicious-site.com/phishing',
  'http://www.google.com.malicious-domain.tk/signin',
  'http://amazon.com-verify-account.ml/login',
  'http://paypal.com-secure-login.ga/confirm',
  'http://facebook.com-security-check.cf/verify'
];

/**
 * Generate training dataset
 */
export function generateTrainingData() {
  const X = [];
  const y = [];

  // Process legitimate URLs
  for (const url of legitimateUrls) {
    const result = featureExtractor.extractFeatures(url);
    if (result) {
      X.push(result.featureVector);
      y.push(0); // 0 = legitimate
    }
  }

  // Process phishing URLs
  for (const url of phishingUrls) {
    const result = featureExtractor.extractFeatures(url);
    if (result) {
      X.push(result.featureVector);
      y.push(1); // 1 = phishing
    }
  }

  // Generate additional synthetic data
  const syntheticData = generateSyntheticData(200);
  X.push(...syntheticData.X);
  y.push(...syntheticData.y);

  return { X, y };
}

/**
 * Generate synthetic training data
 */
function generateSyntheticData(count) {
  const X = [];
  const y = [];

  for (let i = 0; i < count; i++) {
    const isPhishing = Math.random() > 0.5;
    const features = generateSyntheticFeatures(isPhishing);
    X.push(features);
    y.push(isPhishing ? 1 : 0);
  }

  return { X, y };
}

/**
 * Generate synthetic feature vector
 */
function generateSyntheticFeatures(isPhishing) {
  if (isPhishing) {
    return [
      50 + Math.random() * 100,  // urlLength (longer)
      15 + Math.random() * 30,   // hostnameLength
      20 + Math.random() * 50,   // pathLength
      3 + Math.floor(Math.random() * 5),  // numDots
      1 + Math.floor(Math.random() * 4),  // numHyphens
      Math.floor(Math.random() * 3),      // numUnderscores
      3 + Math.floor(Math.random() * 5),  // numSlashes
      5 + Math.floor(Math.random() * 10), // numDigits
      Math.floor(Math.random() * 5),      // numSpecialChars
      2 + Math.floor(Math.random() * 4),  // numSubdomains
      Math.random() > 0.7 ? 1 : 0,        // hasHttps (less likely)
      Math.random() > 0.5 ? 1 : 0,        // hasWww
      Math.random() > 0.8 ? 1 : 0,        // hasIPAddress
      Math.random() > 0.3 ? 1 : 0,        // hasSuspiciousKeywords
      Math.floor(Math.random() * 4),      // numSuspiciousKeywords
      Math.random() > 0.4 ? 1 : 0,        // hasSuspiciousTLD
      Math.random() * 3,                  // domainAge (low)
      0,                                  // isTrustedDomain
      Math.random() > 0.8 ? 1 : 0,        // hasAtSymbol
      Math.random() > 0.9 ? 1 : 0,        // hasDoubleSlash
      Math.random() > 0.8 ? 1 : 0,        // hasPortNumber
      Math.random() > 0.4 ? 1 : 0,        // hasQueryString
      Math.floor(Math.random() * 50),     // queryLength
      Math.floor(Math.random() * 5),      // numQueryParams
      4 + Math.random() * 2,              // urlEntropy (high)
      Math.random() > 0.6 ? 1 : 0,        // hasBrandInSubdomain
      Math.random() > 0.8 ? 1 : 0,        // isShortened
      Math.random() > 0.9 ? 1 : 0,        // hasPunycode
      Math.random() > 0.85 ? 1 : 0        // hasSuspiciousExtension
    ];
  } else {
    return [
      20 + Math.random() * 40,  // urlLength (shorter)
      10 + Math.random() * 15,  // hostnameLength
      5 + Math.random() * 20,   // pathLength
      2 + Math.floor(Math.random() * 2),  // numDots
      Math.floor(Math.random() * 2),      // numHyphens
      0,                                  // numUnderscores
      2 + Math.floor(Math.random() * 2),  // numSlashes
      Math.floor(Math.random() * 5),      // numDigits
      0,                                  // numSpecialChars
      Math.floor(Math.random() * 2),      // numSubdomains
      Math.random() > 0.1 ? 1 : 0,        // hasHttps (more likely)
      Math.random() > 0.3 ? 1 : 0,        // hasWww
      0,                                  // hasIPAddress
      Math.random() > 0.9 ? 1 : 0,        // hasSuspiciousKeywords
      0,                                  // numSuspiciousKeywords
      0,                                  // hasSuspiciousTLD
      5 + Math.random() * 5,              // domainAge (high)
      Math.random() > 0.5 ? 1 : 0,        // isTrustedDomain
      0,                                  // hasAtSymbol
      0,                                  // hasDoubleSlash
      0,                                  // hasPortNumber
      Math.random() > 0.5 ? 1 : 0,        // hasQueryString
      Math.floor(Math.random() * 20),     // queryLength
      Math.floor(Math.random() * 3),      // numQueryParams
      3 + Math.random() * 1,              // urlEntropy (low)
      0,                                  // hasBrandInSubdomain
      0,                                  // isShortened
      0,                                  // hasPunycode
      0                                   // hasSuspiciousExtension
    ];
  }
}

/**
 * Split data into training and test sets
 */
export function trainTestSplit(X, y, testSize = 0.2) {
  const indices = Array.from({ length: X.length }, (_, i) => i);
  
  // Shuffle indices
  for (let i = indices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [indices[i], indices[j]] = [indices[j], indices[i]];
  }

  const splitIndex = Math.floor(X.length * (1 - testSize));

  const trainIndices = indices.slice(0, splitIndex);
  const testIndices = indices.slice(splitIndex);

  return {
    X_train: trainIndices.map(i => X[i]),
    X_test: testIndices.map(i => X[i]),
    y_train: trainIndices.map(i => y[i]),
    y_test: testIndices.map(i => y[i])
  };
}
